/**
 * Asking for an arrangement in words: "a wind quintet, fill in the harmony, arpeggios".
 *
 * `POST /theory/ask` hands the words, with the roster and settings as they stand and what the
 * board knows of the score, to a language model, in any language the reader writes in: one on
 * the same machine unless the server is set another (its `ask/providers`). It answers with the roster and settings the words ask for, or, where
 * the words can be read two ways that would arrange differently, with one question and the
 * answers to choose from. Nothing is arranged by the route: the board arranges what it is
 * answered, as the reader asked, and shows what was changed.
 *
 * The settings are every switch the board has, the note model's among them: where arranging
 * starts, which layers of the score the model is told, how far, and how much more than the score
 * its players write (`ArrangeFollow`). So the
 * model that reads the words is the layer that decides how much of each is put on, and what it
 * decides is the same switches a reader moves by hand. So are the wishes no switch of the
 * arranger's answers (`ArrangeWishes`): the key, how the players sound their notes, and an
 * easier page.
 */

import type { ArrangeLayer, ArrangeMore, ArrangeStart } from "./ai";
import type { Counter, HarmonyLevel, Texture, Variation } from "./arrange";
import type { ArrangeKey, Ease, Playing } from "./wishes";

/** Every setting of an arrangement bar the roster, as the Lab board holds them. */
export interface AskSettings {
  harmony: HarmonyLevel;
  texture: Texture;
  follow: boolean;
  rise: boolean;
  moving: boolean;
  stops: boolean;
  double: boolean;
  share: boolean;
  counter: Counter;
  vary: Variation;
  /** The whole piece, rather than bars `first` to `last`. */
  whole: boolean;
  /** The first bar arranged, counted from 1. */
  first: number;
  /** The last bar arranged, from `first` up. */
  last: number;
  /** Where arranging starts. */
  start: ArrangeStart;
  /** Starting from the note model: the layers of the score it is told. */
  layers: ArrangeLayer[];
  /** Starting from the note model: how far what it is told is put on, from 0 to 1. */
  strength: number;
  /** Starting from the note model: how much more than the score its players write, `null` for no more. */
  more: ArrangeMore | null;
  /** How the players sound their notes (`ArrangeWishes`); not starting from the tune. */
  playing: Playing;
  /** The instruments `playing` is asked of; empty for every player. */
  playingWho: string[];
  /** How hard the page may be to play; not starting from the tune. */
  ease: Ease;
  /** The key to arrange in, or `null` for the score's own. */
  key: ArrangeKey | null;
}

/** The most characters a request in words may run to. The server refuses a longer one. */
export const ASK_LIMIT = 500;

/** What the board knows of the score open on it. */
export interface AskScore {
  /** How many bars it has. */
  bars: number;
  /** Its parts, as the file names them. */
  parts: string[];
}

/** A question the words were answered with, and the answer the reader chose. */
export interface AskAnswered {
  question: string;
  choice: string;
}

export interface AskRequest {
  words: string;
  /** The roster as it stands, one seat per player, as `ArrangeRequest.roster` names them. */
  roster: string[];
  settings: AskSettings;
  /** The score open on the board, or `null` for none yet. */
  score: AskScore | null;
  /** Where these words were answered with a question: it and the reader's choice. Words
   * answered once are never answered with another question. */
  answered: AskAnswered | null;
}

/** One answer to a question back. */
export interface AskChoice {
  /** As the reader is shown it, in the language of the words. */
  label: string;
  /** What it means, in English, as it is sent back in `AskAnswered.choice`: a translation the
   * model got wrong can mislabel a button, never change what choosing it asks for. */
  words: string;
}

/** One question back, in the language of the words, and the answers to choose from. */
export interface AskQuestion {
  text: string;
  /** Two or more. */
  choices: AskChoice[];
}

/** What `POST /theory/ask` answers. */
export interface AskPayload {
  /** The roster the words ask for, one seat per player; the roster sent where they ask none,
   * or where they are answered with a question. */
  roster: string[];
  /** The settings the words ask for; those sent where they are answered with a question. */
  settings: AskSettings;
  /** A question the words must be answered first, or `null` where they were read. */
  question: AskQuestion | null;
  /** What was asked for that no setting can do, in a sentence, or `null` for nothing. */
  unmet: string | null;
  /** The model that read the words. */
  model: string;
}
